<template>
  <div class="lead-manager">
    <h1>{{ trans('dashboard.leads.title') }}</h1>
    <div class="lead-manager__header lmh">
      <div class="lmh__controls lmhc">
        <div class="lmhc__left lmhcl">
          <InputSearch
            class="lmhcl__search"
            :placeholder="trans('user.inbox.searchbar.placeholder')"
            @search-input="onSearchInput"
          />
          <AddressFilter class="lmhcl__address-filter" />
        </div>
        <button
          class="lmhc__list-filters"
          :class="{ 'btn--with-icon': !countActiveFilters }"
          @click="openFilters()"
        >
          <span v-if="countActiveFilters > 0" class="pin">{{ countActiveFilters }}</span>
          <span v-else class="icon" />
          {{ trans('filter.button.filters') }}
        </button>
        <LeadFiltersModal
          v-model="searchCriterion"
          :is-opened="showFiltersModal"
          :archive-link="archiveLink"
          @close="closeFilters"
        />
        <div class="lmhc__right lmhcr">
          <div class="input-checkbox">
            <input
              id="include-archive-deals-btn"
              v-model="displayArchived"
              :disabled="loading"
              type="checkbox"
              class="lmhcr__archive"
              @change="onArchivedCheckboxChange"
            >
            <label for="include-archive-deals-btn">
              {{ trans('user.leads.display_archived') }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="lead-manager__content">
      <div v-for="state in states" :key="state" class="lead-manager__col lmc">
        <SpinnerContainer v-if="loading && [ state, 'all' ].includes(loadingMode)" />
        <ColumnHeader :code="state" :counter="totalRecords[state]" />
        <div
          :id="`lead-manager-col--${state}`"
          :ref="`column-${state}`"
          class="lmc__content"
          :data-state="state"
          @scroll="onScroll($event, state)"
        >
          <LeadCard
            v-for="(deal, index) in leads[state]"
            :key="deal[0].id"
            :meta="deal"
            :deal="deal[0]"
            :states="states"
            :data-id="deal[0].id"
            :display-archived="displayArchived"
            :state="state"
            :index="index"
            is-seller
            @on-archive-card="onCardToArchive"
          />
        </div>
      </div>
    </div>
    <card-actions-container
      :payload="cardActionPayload"
      @card-action-cancelled="onCardActionCancelled"
      @card-action-completed="onCardActionCompleted"
    />
  </div>
</template>

<script>
import { DealStep } from '@enums/dealSteps';
import { storeToRefs } from 'pinia';
// eslint-disable-next-line import/no-extraneous-dependencies
import Sortable from 'sortablejs';
import InputSearch from '@components/InputSearch.vue';
import SpinnerContainer from '@components/SpinnerContainer.vue';
import AddressFilter from '@components-molecules/AddressFilter.vue';
import { useAddressFilterStore } from '@stores/useAddressFilterStore';
import { eventBus } from '@tools/event-bus';
import { translationMixin } from '@elements/translation';
import { URL_USER_INBOX_ARCHIVED, URL_LEADS } from '@tools/routes/routes';
import { Requests } from '@tools/requests';
import { Logger } from '@tools/logger/logger';
import LeadCard from './LeadCard.vue';
import ColumnHeader from './ColumnHeader.vue';
import LeadFiltersModal from './LeadFiltersModal.vue';
import CardActionsContainer from '../deal-dashboard/card/CardActionsContainer.vue';
import { ACTIONS } from '../deal-dashboard/actions/constants';
import { CardActionPayload } from '../deal-dashboard/card/CardActionPayload';
import { COLUMN__SIGNED, COLUMN__UNSIGNED, EVENTS } from '../deal-dashboard/constants';

const ALL_COLUMNS = 'all';

export default {
  components: {
    CardActionsContainer,
    LeadCard,
    ColumnHeader,
    InputSearch,
    SpinnerContainer,
    AddressFilter,
    LeadFiltersModal,
  },
  mixins: [ translationMixin ],
  setup() {
    const { selectedAddressesIds } = storeToRefs(useAddressFilterStore());
    return {
      selectedAddressesIds,
    };
  },
  data() {
    return {
      loading: false,
      loadingMode: null,
      searchCriterion: '',
      currentPage: { in_progress: 1, visited: 1, signed: 1, unsigned: 1 },
      lastPageReached: {
        in_progress: false,
        visited: false,
        signed: false,
        unsigned: false,
      },
      totalRecords: { in_progress: 0, visited: 0, signed: 0, unsigned: 0 },
      leads: { in_progress: [], visited: [], signed: [], unsigned: [] },
      states: [ 'in_progress', 'visited', 'signed', 'unsigned' ],
      showFiltersModal: false,
      cardActionPayload: null,
      displayArchived: false,
    };
  },
  computed: {
    countActiveFilters() {
      let activeFilters = 0;
      if (this.selectedAddressesIds.length > 0) {
        activeFilters++;
      }

      if (this.searchCriterion !== '') {
        activeFilters++;
      }

      return activeFilters;
    },
    archiveLink() {
      return URL_USER_INBOX_ARCHIVED;
    },
  },
  watch: {
    selectedAddressesIds() {
      if (!this.addressTimer) {
        clearTimeout(this.addressTimer);
      }
      this.addressTimer = setTimeout(() => {
        this.resetLeads(ALL_COLUMNS);
        this.fetchLeads(1);
      }, 500);
    },
    searchCriterion(value) {
      if (this.searchTimeout !== null) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.searchCriterion = value;
        this.refreshLeads(ALL_COLUMNS);
      }, 500);
    },
  },
  async mounted() {
    eventBus.addEventListener(EVENTS.CARD_MOVED, this.onCardMoved);
    this.resetLeads(ALL_COLUMNS);
    await this.setLeads(window.initialLeadsState.deals, window.initialLeadsState.total_records);
    await this.createSortable();
  },
  methods: {
    onArchivedCheckboxChange() {
      this.refreshLeads(ALL_COLUMNS);
    },
    locateDealById(dealId) {
      let column = null;
      let index = null;
      for (const [ c, leads ] of Object.entries(this.leads)) { // eslint-disable-line no-restricted-syntax
        leads.forEach((d, i) => { // eslint-disable-line no-loop-func
          if (d[0].id === dealId) {
            column = c;
            index = i;
          }
        });
        if (column !== null && index !== null) {
          break;
        }
      }
      return [ column, index ];
    },
    onCardToArchive(dealId) {
      const payload = new CardActionPayload();
      [ payload.fromColumn, payload.fromColumnIndex ] = this.locateDealById(dealId);
      // eslint-disable-next-line prefer-destructuring
      payload.deal = this.getLocalDealById(dealId)[0];
      payload.interlocutor = payload.deal.buyer;
      payload.action = [ COLUMN__SIGNED, COLUMN__UNSIGNED ].includes(payload.fromColumn)
        ? ACTIONS.DEAL__ARCHIVE : ACTIONS.DEAL__ARCHIVE_UI;
      // Trigger the action
      this.cardActionPayload = payload;
    },
    async createSortable() {
      await this.$nextTick();
      this.states.forEach((state) => {
        const elementRef = this.$refs[`column-${state}`];
        if (elementRef && elementRef.length) {
          // eslint-disable-next-line no-new
          new Sortable(elementRef[0], {
            group: 'shared',
            onEnd: (event) => {
              this.onCardDropped(event);
            },
            ghostClass: 'lead__placeholder',
          });
        }
      });
    },
    setLeads(deals, totalRecords, state) {
      deals.forEach((deal) => {
        const dealState = deal[0].state === DealStep.CONTACTED ? DealStep.IN_PROGRESS : deal[0].state;

        let alreadyExist = false;
        this.leads[dealState].forEach((lead) => {
          if (lead[0].id === deal[0].id) {
            alreadyExist = true;
          }
        });

        if (!alreadyExist) {
          this.leads[dealState].push(deal);
        }
      });

      this.totalRecords = { ...this.totalRecords, ...totalRecords };

      if (!state || state === ALL_COLUMNS) {
        Object.keys(this.totalRecords).forEach((recordState) => {
          this.lastPageReached[recordState] = this.totalRecords[recordState] === this.leads[recordState].length;
        });

        return;
      }

      this.lastPageReached[state] = this.leads[state].length === this.totalRecords[state];
    },
    resetLeads(columnToReset) {
      if (columnToReset === ALL_COLUMNS) {
        this.leads = { in_progress: [], visited: [], signed: [], unsigned: [] };
        this.totalRecords = { in_progress: 0, visited: 0, signed: 0, unsigned: 0 };
        this.currentPage = { in_progress: 1, visited: 1, signed: 1, unsigned: 1 };
        return;
      }
      this.leads[columnToReset] = [];
      this.totalRecords[columnToReset] = 0;
      this.currentPage[columnToReset] = 1;
    },
    buildSearchParam() {
      const urlSearchParams = new URLSearchParams();
      if (this.searchCriterion) {
        urlSearchParams.append('search', this.searchCriterion);
      }
      if (this.selectedAddressesIds) {
        urlSearchParams.append('addresses', this.selectedAddressesIds);
      }
      if (this.displayArchived) {
        urlSearchParams.append('archived', true);
      }

      return `?${urlSearchParams.toString()}`;
    },
    fetchLeads(page, state) {
      this.loadingMode = state ?? ALL_COLUMNS;
      this.loading = true;

      Requests
        .get(URL_LEADS.replace('{page}', page) + this.buildSearchParam(state))
        .then((response) => {
          if (response.status === 200) {
            return response;
          }
          return Logger.error(Error(response));
        })
        .then((json) => {
          this.setLeads(json.data.deals, json.data.total_records, state);

          // Change data in page
          this.currentPage[state] = page;
        })
        .catch((error) => {
          Logger.error(Error(error));
        })
        .finally(() => {
          this.loading = false;
          this.loadingMode = null;
        });
    },
    onCardDropped(event) {
      eventBus.dispatchEvent(
        new CustomEvent(EVENTS.CARD_MOVED, {
          detail: {
            dealId: parseInt(event.item.dataset.dealId, 10),
            to: event.to.dataset.state,
            from: event.from.dataset.state,
            oldIndex: event.oldIndex,
          },
        }),
      );
    },
    onCardMoved(event) {
      const { dealId, to, from, fromIndex } = event.detail;
      let index = fromIndex;

      // The card was "moved" without drag'n'drop (through mobile view)
      // We have to perform what the draggable lib does when a card is dropped.
      // Find the deal
      index = this.leads[from].findIndex((d) => d[0].id === dealId);
      if (index === -1) {
        return;
      }
      // Move the deal to the first place (arbitrary position) in its new column
      this.moveDeal(to, 0, from, index);

      const payload = new CardActionPayload();
      payload.fromColumn = from;
      payload.fromColumnIndex = index;
      payload.toColumn = to;
      // eslint-disable-next-line prefer-destructuring
      payload.deal = this.getLocalDealById(dealId)[0];
      payload.interlocutor = payload.deal.buyer;
      // This will trigger a watcher in <card-actions-container>
      this.cardActionPayload = payload;
    },
    getLocalDealById(dealId) {
      let deal = null;
      Object.values(this.leads).forEach((arr) => {
        deal = deal || arr.find((d) => d[0].id === dealId);
      });
      return deal;
    },
    moveDeal(toColumn, toIndex, fromColumn, fromIndex) {
      if (fromColumn === toColumn) {
        return;
      }
      const back = this.leads[fromColumn][fromIndex];
      // Remove deal from its previous column
      this.leads[fromColumn].splice(fromIndex, 1);
      // Insert deal at its new column
      this.leads[toColumn].splice(toIndex, 0, back);
    },
    onSearchInput(value) {
      if (typeof value !== 'string') {
        return;
      }
      this.searchCriterion = value;
    },
    refreshLeads(columnToRefresh) {
      this.resetLeads(columnToRefresh);
      this.fetchLeads(1, columnToRefresh);
    },
    onCardActionCancelled(toRevert) {
      if (toRevert.fromColumn && toRevert.toColumn) {
        // Find the deal
        const index = this.leads[toRevert.toColumn].findIndex((d) => d[0].id === toRevert.deal.id);
        if (index > -1) {
          // Reintegrate the deal where it belongs
          // It's a revert, so from & to are inverted.
          this.moveDeal(toRevert.fromColumn, toRevert.fromColumnIndex, toRevert.toColumn, index);
        }
      }
      this.resetCardActionPayload();
    },
    resetCardActionPayload() {
      this.cardActionPayload = null;
    },
    /**
     * @param {CardActionPayload} originalPayload
     */
    onCardActionCompleted(originalPayload) {
      this.resetCardActionPayload();
      if (!originalPayload.next) {
        if (originalPayload.action === ACTIONS.DEAL__ARCHIVE) {
          // Refresh all dashboard
          this.refreshLeads(ALL_COLUMNS);
          return;
        }
        // Refresh updated columns
        if (originalPayload.toColumn) {
          this.refreshLeads(originalPayload.toColumn);
        }
        if (originalPayload.fromColumn) {
          this.refreshLeads(originalPayload.fromColumn);
        }
        return;
      }

      // Locate deal and update payload
      /** @type {CardActionPayload} next */
      const { next } = originalPayload;
      [ next.fromColumn, next.fromColumnIndex ] = this.locateDealById(originalPayload.deal.id);
      if (next.toColumn) {
        // If a "to" column is specified, move the deal to keep the UI up-to-date.
        this.moveDeal(next.toColumn, 0, next.fromColumn, next.fromColumnIndex);
      }
      // Trigger the new action
      this.cardActionPayload = next;
    },
    closeFilters() {
      this.showFiltersModal = false;
    },
    openFilters() {
      this.showFiltersModal = true;
    },
    onScroll(event, state) {
      const element = event.target;
      const bottomOfWindow = element.scrollHeight - element.scrollTop <= element.clientHeight;
      if (bottomOfWindow && this.loadingMode !== ALL_COLUMNS) {
        const stateFormatted = state === DealStep.CONTACTED ? DealStep.IN_PROGRESS : state;
        this.fetchLeads(this.currentPage[stateFormatted] + 1, stateFormatted);
      }
    },
  },
};
</script>
